import * as React from 'react';
import styled from '@emotion/styled';
import { FeatureLinks, featureLinksHover } from './FeatureLinks';
import { FeatureNodeType, FEATURE_NODE_LABELS } from '../../../data/features';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface FeatureSectionWidgetProps {
  icon: IconProp;
  label: string;
  desc?: string;
  nodeType?: FeatureNodeType;
  id?: string;
  connections?: React.ReactNode;
  link?: string;
  docsLink?: string;
}

export const FeatureSectionWidget: React.FC<React.PropsWithChildren<FeatureSectionWidgetProps>> = (props) => (
  <S.Container id={props.id} nodeType={props.nodeType}>
    <S.TopContent data-feature-header>
      {props.nodeType ? <S.Kind nodeType={props.nodeType}>{FEATURE_NODE_LABELS[props.nodeType]}</S.Kind> : null}
      <S.Top>
        <S.Icon nodeType={props.nodeType} icon={props.icon} aria-hidden="true" />
        <S.Label>{props.label}</S.Label>
        <FeatureLinks docsLink={props.docsLink} link={props.link} label={props.label} nodeType={props.nodeType} />
      </S.Top>
      {props.desc ? <S.Desc>{props.desc}</S.Desc> : null}
    </S.TopContent>
    {props.connections}
    {React.Children.count(props.children) > 0 ? <S.Children>{props.children}</S.Children> : null}
  </S.Container>
);
namespace S {
  export const Container = styled.section<{ nodeType?: FeatureNodeType }>`
    ${featureLinksHover};
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
    padding: ${(p) => (p.nodeType ? '20px' : '0')};
    background: ${(p) => (p.nodeType ? p.theme.featureGraph[p.nodeType].background : 'transparent')};
    border: ${(p) => (p.nodeType ? `1px solid ${p.theme.featureGraph[p.nodeType].border}` : 'none')};
    border-radius: 18px;
  `;
  export const TopContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;
  export const Kind = styled.span<{ nodeType: FeatureNodeType }>`
    color: ${(p) => p.theme.featureGraph[p.nodeType ?? FeatureNodeType.FEATURE].accent};
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  `;
  export const Desc = styled.div`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 14px;
    line-height: 1.5;
  `;
  export const Label = styled.h2`
    margin: 0;
    font-size: 20px;
    color: ${(p) => p.theme.text.heading};
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    flex: 1;
    min-width: 0;
  `;
  export const Top = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  `;
  export const Icon = styled(FontAwesomeIcon)<{ nodeType?: FeatureNodeType }>`
    color: ${(p) => p.theme.featureGraph[p.nodeType ?? FeatureNodeType.FEATURE].accent};
    font-size: 22px;
    flex-shrink: 0;
    width: 24px;
  `;
  export const Children = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
    gap: 12px;
    align-items: start;
  `;
}
